import {
  IHouseStepPrices,
  IWizardHouseState,
  TStepErrors,
  TStepPayload,
  TWizardStepComponentEditable,
  TWizardStepComponentEditableView,
  WIZARD_HOUSE_STEPS_PATHS
} from "../../../interfaces";
import { Form, Input } from "antd";
import { StepViewOnlyValue } from "../../../Student/styles";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  createCheckIsReadonlyStepField,
  getChangedFields,
  getStepErrorsFromResponse
} from "../../../utils";

import { HouseAPI } from "../../../../../API/houseApi";
import styled from "styled-components/macro";
import { PriceCalculation as PriceCalculationOriginal } from "../../../components";

const statePath = WIZARD_HOUSE_STEPS_PATHS.Prices;
export const WizardStepPrices: TWizardStepComponentEditable<IWizardHouseState> =
  forwardRef(({ state, readonly, viewonly }, ref) => {
    const [values, setValues] = useState<TStepPayload<IHouseStepPrices>>(
      state[statePath] ?? {}
    );
    const [errors, setErrors] = useState<TStepErrors<typeof values>>(null);

    useImperativeHandle(ref, () => ({
      save: () => {
        setErrors(null);
        return HouseAPI.wizardSavePrices(
          state.house_id!,
          state.wizard_type!,
          values
        )
          .then((result) => {
            return {
              ...state,
              [statePath]: result
            };
          })
          .catch((e) => {
            setErrors(getStepErrorsFromResponse(e));
            return Promise.reject(e);
          });
      },
      savePartially: () => {
        setErrors(null);
        return HouseAPI.wizardSavePricesPartially(
          state.house_id!,
          state.wizard_type!,
          getChangedFields(state[statePath], values)
        )
          .then((result) => {
            return {
              ...state,
              [statePath]: result
            };
          })
          .catch((e) => {
            setErrors(getStepErrorsFromResponse(e));
            return Promise.reject(e);
          });
      }
    }));

    return (
      <WizardStepPricesView
        values={values}
        setValues={setValues}
        errors={errors}
        readonly={readonly}
        viewonly={viewonly}
      />
    );
  });
const PriceCalculation = styled(PriceCalculationOriginal)`
  width: 300px;
  flex-shrink: 0;
  align-self: flex-start;
`;
const Root = styled.div`
  display: flex;
  gap: 40px;

  @media (max-width: 768px) {
    flex-direction: column;

    ${PriceCalculation} {
      width: 100%;
    }
  }

  .ant-form-item-control-input-content {
    display: flex;
    align-items: flex-start;
    gap: 16px;

    .ant-input-affix-wrapper {
      width: 130px;
      flex-shrink: 0;
    }
  }
`;

const InputHint = styled.div`
  font-size: 16px;
  color: ${(p) => p.theme.colors.midGrey};
`;

export const WizardStepPricesView: TWizardStepComponentEditableView<
  IHouseStepPrices
> = ({ errors, values, setValues, viewonly, readonly, enabledFields }) => {
  const { t } = useTranslation("house");

  const inputSize = "large";
  const isReadonly = createCheckIsReadonlyStepField(readonly, enabledFields);

  return (
    <Root>
      <Form layout="vertical">
        <Form.Item
          label={t(`wizard.steps.Prices.form.price.title`)}
          validateStatus={errors?.price ? "error" : ""}
          help={errors?.price ? t("wizard.errors.decimal") : ""}
        >
          {viewonly ? (
            <StepViewOnlyValue>€{values.price ?? ""}</StepViewOnlyValue>
          ) : (
            <Input
              size={inputSize}
              disabled={isReadonly("price")}
              value={values.price ?? undefined}
              placeholder={t(`wizard.steps.Prices.form.price.placeholder`, {
                defaultValue: ""
              })}
              type="number"
              prefix="€"
              onFocus={(e) => {
                if (e.target.value === "0") {
                  setValues({
                    ...values,
                    price: ""
                  });
                }
              }}
              onChange={(e) => {
                setValues({
                  ...values,
                  price: e.target.value
                });
              }}
            />
          )}
          <InputHint>{t(`wizard.steps.Prices.form.price.hint`)}</InputHint>
        </Form.Item>
        <Form.Item
          label={t(`wizard.steps.Prices.form.additional_expenses.title`)}
          validateStatus={errors?.additional_expenses ? "error" : ""}
          help={errors?.additional_expenses ? t("wizard.errors.decimal") : ""}
        >
          {viewonly ? (
            <StepViewOnlyValue>
              €{values.additional_expenses ?? ""}
            </StepViewOnlyValue>
          ) : (
            <Input
              size={inputSize}
              disabled={isReadonly("additional_expenses")}
              value={values.additional_expenses ?? undefined}
              placeholder={t(
                `wizard.steps.Prices.form.additional_expenses.placeholder`,
                { defaultValue: "" }
              )}
              type="number"
              prefix="€"
              onFocus={(e) => {
                if (e.target.value === "0") {
                  setValues({
                    ...values,
                    additional_expenses: ""
                  });
                }
              }}
              onChange={(e) => {
                setValues({
                  ...values,
                  additional_expenses: e.target.value
                });
              }}
            />
          )}
          <InputHint>
            {t(`wizard.steps.Prices.form.additional_expenses.hint`)}
          </InputHint>
        </Form.Item>
        <Form.Item
          label={t(`wizard.steps.Prices.form.service_costs.title`)}
          validateStatus={errors?.service_costs ? "error" : ""}
          help={errors?.service_costs ? t("wizard.errors.decimal") : ""}
        >
          {viewonly ? (
            <StepViewOnlyValue>€{values.service_costs ?? ""}</StepViewOnlyValue>
          ) : (
            <Input
              size={inputSize}
              disabled={isReadonly("service_costs")}
              value={values.service_costs ?? undefined}
              placeholder={t(
                `wizard.steps.Prices.form.service_costs.placeholder`,
                { defaultValue: "" }
              )}
              type="number"
              prefix="€"
              onFocus={(e) => {
                if (e.target.value === "0") {
                  setValues({
                    ...values,
                    service_costs: ""
                  });
                }
              }}
              onChange={(e) => {
                setValues({
                  ...values,
                  service_costs: e.target.value
                });
              }}
            />
          )}
          <InputHint>
            {t(`wizard.steps.Prices.form.service_costs.hint`)}
          </InputHint>
        </Form.Item>
        <Form.Item
          label={t(`wizard.steps.Prices.form.deposit.title`)}
          validateStatus={errors?.deposit ? "error" : ""}
          help={errors?.deposit ? t("wizard.errors.decimal") : ""}
        >
          {viewonly ? (
            <StepViewOnlyValue>€{values.deposit ?? ""}</StepViewOnlyValue>
          ) : (
            <Input
              size={inputSize}
              disabled={isReadonly("deposit")}
              value={values.deposit ?? undefined}
              placeholder={t(`wizard.steps.Prices.form.deposit.placeholder`, {
                defaultValue: ""
              })}
              type="number"
              prefix="€"
              onFocus={(e) => {
                if (e.target.value === "0") {
                  setValues({
                    ...values,
                    deposit: ""
                  });
                }
              }}
              onChange={(e) => {
                setValues({
                  ...values,
                  deposit: e.target.value
                });
              }}
            />
          )}
          <InputHint>{t(`wizard.steps.Prices.form.deposit.hint`)}</InputHint>
        </Form.Item>
      </Form>
      <PriceCalculation values={values} />
    </Root>
  );
};
